import {
  Box,
  Button,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { API } from "aws-amplify";
import { useButtonLocationDialog } from "hooks/useButtonLocationDialog";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useRegions } from "hooks/useRegions";
import { useSpecialistsAndOrganizations } from "hooks/useSpecialistsAndOrganizations";
import { ReactComponent as Slide4 } from "images/adminIntro4.svg";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { StepIndicator, StepIndicatorActive } from "SC";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { DialogWindow } from "UI/DialogWindow";
import { ProviderSelector } from "UI/ProviderSelector";
import { SenderSelector } from "UI/SenderSelector";
import { SpecialistSelector } from "UI/SpecialistSelector";
import * as FP from "utils/fp-js";
import { convertDeviceType, getSpecialistReducer } from "utils/helpers";
import { AddNewOriginatorDialog } from "./AddNewOriginatorDialog";
import { AddNewPracticeDialog } from "./AddNewPracticeDialog";
import { AddNewProviderDialog } from "./AddNewProviderDialog";
import PracticeSelector from "./PracticeSelector";
import { useAuthenticator } from "auth/useAuthenticator";
import { customColors } from "models/customColors";

export const Title = styled(Box)`
  color: ${customColors.text};
  text-align: center;
  font-family: "Inter Bold";
  font-size: 26px;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

export const Info = styled(Box)`
  margin-top: 15px;
  padding: 0 20px;

  min-height: 80px;

  color: ${customColors.infoText};
  text-align: center;
  font-family: Inter;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const steps = {
  ASSIGN_PRACTICE: 0,
  ASSIGN_PROVIDER: 1,
  ASSIGN_SENDER: 2,
  ASSIGN_SPECIALISTS: 3,
  ADD_BUTTON: 4,
};

const defaultFormData = {
  deviceId: "",
  deviceType: "",
  organizationId: "",
};

export const AddNewButtonLocationDialog = ({
  buttonId,
  buttonType,
  handleClose,
  refreshList,
  setCurrentButtonId,
  selectedPractice = null,
}) => {
  const { handleAuthError } = useAuthenticator();
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const spinner = useAppSelector((state) => state.spinner);
  const { showWarning, openConfirmationWithCallback } =
    useContext(GlobalContext);
  const { regionsOptions, refreshRegionsOptions } = useRegions();
  const [currentStep, setCurrentStep] = useState(0);

  const {
    selectedSenders,
    setSelectedSenders,
    setNewlyAddedSender,
    selectedProviders,
    setSelectedProviders,
    setNewlyAddedProvider,
    admins,
    setAdmins,
    marketers,
    setMarketers,
    schedulers,
    setSchedulers,
    handleChangeSpecialist,
    handleChangeProvider,
    isUpdateButtonDisabled,
    isMarketerSelected,
    isSchedulerSelected,
    isAdminSelected,
    isSenderSelected,
    isProviderSelected,
    selectedSpecialists,
  } = useButtonLocationDialog();

  const [currentPractice, setCurrentPractice] = useState({ id: "", name: "" });
  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);
  const [isAddNewProviderDialogOpen, setIsAddNewProviderDialogOpen] =
    useState(false);

  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const practiceId = watch("organizationId");
  const deviceType = watch("deviceType");

  const {
    originators,
    providers,
    specialists,
    loadData,
    isOriginatorsLoading,
    setIsOriginatorsLoading,
    isProvidersLoading,
    setIsProvidersLoading,
  } = useSpecialistsAndOrganizations({ practiceId });

  const getSelectedIdsByRole = (role) => {
    return specialists
      .filter((specialist) => specialist?.role === role)
      .filter(({ id }) => selectedSpecialists.includes(id))
      .map(({ id }) => id);
  };

  const getSelectedSpecialistIds = (specialists) =>
    Object.keys(specialists).filter((id) => specialists[id]?.selected);

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    const buttonLocation = {
      ...getValues(),
      admins: getSelectedIdsByRole("Admin"),
      marketers: getSelectedIdsByRole("Marketer"),
      schedulers: getSelectedIdsByRole("Scheduler"),
      senders: getSelectedSpecialistIds(selectedSenders),
      providers: getSelectedSpecialistIds(selectedProviders),
    };

    const result = await API.put("restApi", "/button_locations", {
      body: {
        buttonLocation,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Success
          </Typography>
        </Stack>
      ),
      content: <>Button added successfully</>,
    });

    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  const setSelectedPractice = ({ id, name }) => {
    setCurrentPractice({ id, name });
    setValue("organizationId", id, { shouldDirty: true });
  };

  const handleCheckIfLocationExists = async () => {
    const { deviceType, deviceId } = getValues();
    const providerId = Object.keys(selectedProviders).find(
      (id) => selectedProviders[id].selected
    );

    if (!providerId) {
      dispatch(showError("Please select a provider"));
      return;
    }

    dispatch(showSpinner());
    try {
      const result = await API.post("restApi", "/button_locations_by_sender", {
        body: {
          providerId,
          deviceType,
          deviceId: String(deviceId)?.trim() ?? "",
        },
      });

      if (result?.error) {
        console.error(result.error);
        dispatch(showError(result.error));
        return;
      }

      if (
        result?.length > 0 &&
        ["Virtual", "Soft", "Digital"].includes(deviceType)
      ) {
        const locationId = result[0]?.id;
        if (locationId) {
          openConfirmationWithCallback({
            title: "Digital button for this Provider already exists",
            message:
              "You can edit the existing digital button or delete and create a new one.",
            callbackFn: () => {
              forceCloseAndReset();
              setCurrentButtonId(locationId);
            },
            buttonLabels: ["Cancel", "Edit"],
          });
          return;
        }
      }

      if (
        result?.length > 0 &&
        ["Real", "Hard", "Physical"].includes(deviceType)
      ) {
        dispatch(
          showError(
            "This physical button is already assigned to another Provider"
          )
        );
        return;
      }

      setCurrentStep(currentStep + 1);
    } catch (error) {
      handleAuthError(error);
    } finally {
      dispatch(hideSpinner());
    }
  };

  useEffect(() => {
    if (buttonId && buttonType) {
      reset({
        ...defaultFormData,
        deviceId: buttonId,
        deviceType: buttonType,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonId, buttonType]);

  useEffect(() => {
    if (!originators?.length) {
      setSelectedSenders({});
      return;
    }
    const senderList = originators
      .filter(({ organizationId }) => organizationId === practiceId)
      .reduce(
        (acc, { id, firstName, lastName, jobTitle, email, phone }) => ({
          ...acc,
          [id]: {
            label: [`${firstName} ${lastName}`, jobTitle, phone, email]
              .filter(Boolean)
              .join(", "),
            selected: false,
          },
        }),
        {}
      );

    setSelectedSenders(senderList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originators, practiceId]);

  useEffect(() => {
    if (!providers?.length) {
      setSelectedProviders({});
      return;
    }

    const providerList = providers
      .filter((item) => item?.practiceId === practiceId)
      .reduce(
        (acc, { id, name, speciality }) => ({
          ...acc,
          [id]: {
            label: [name, speciality].filter(Boolean).join(", "),
            selected: false,
          },
        }),
        {}
      );

    setSelectedProviders(providerList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers, practiceId]);

  useEffect(() => {
    if (specialists.length === 0 || !regionsOptions) {
      return;
    }

    const adminList = specialists
      .filter(({ role }) => role === "Admin")
      .reduce(getSpecialistReducer(regionsOptions), {});

    const marketerList = specialists
      .filter(({ role }) => role === "Marketer")
      .reduce(getSpecialistReducer(regionsOptions), {});

    const schedulerList = specialists
      .filter(({ role }) => role === "Scheduler")
      .reduce(getSpecialistReducer(regionsOptions), {});

    setAdmins(adminList);
    setMarketers(marketerList);
    setSchedulers(schedulerList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialists, regionsOptions]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const mobileButtonSx = { padding: "10px 20px" };
  const sx = isMobile ? mobileButtonSx : {};

  useEffect(() => {
    if (
      currentStep === steps.ASSIGN_PRACTICE &&
      deviceType &&
      selectedPractice
    ) {
      setSelectedPractice(selectedPractice);
      setCurrentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, deviceType, selectedPractice]);

  return (
    <DialogWindow
      open={!!buttonId}
      withCloseButton={false}
      handleClose={closeAndReset}
      title={FP.match(currentStep)
        .on(
          steps.ASSIGN_PRACTICE,
          <>
            Assign Practice
            <Stack direction={"row"} gap={1} mt={1.6} mb={1}>
              <StepIndicatorActive />
              <StepIndicator />
              <StepIndicator />
              <StepIndicator />
            </Stack>
          </>
        )
        .on(
          steps.ASSIGN_PROVIDER,
          <>
            Assign Provider
            <Stack direction={"row"} gap={1} mt={1.6} mb={1}>
              <StepIndicatorActive />
              <StepIndicatorActive />
              <StepIndicator />
              <StepIndicator />
            </Stack>
          </>
        )
        .on(
          steps.ASSIGN_SENDER,
          <>
            Assign Sender
            <Stack direction={"row"} gap={1} mt={1.6} mb={1}>
              <StepIndicatorActive />
              <StepIndicatorActive />
              <StepIndicatorActive />
              <StepIndicator />
            </Stack>
          </>
        )
        .on(
          steps.ASSIGN_SPECIALISTS,
          <>
            Assign Specialists
            <Stack direction={"row"} gap={1} mt={1.6} mb={1}>
              <StepIndicatorActive />
              <StepIndicatorActive />
              <StepIndicatorActive />
              <StepIndicatorActive />
            </Stack>
          </>
        )
        .otherwise("Add New Button")}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={1} minWidth={isMobile ? undefined : "480px"}>
            {currentStep === steps.ASSIGN_PRACTICE && (
              <>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  gap={2}
                  mt={3}
                  sx={{ color: "gray" }}
                >
                  {["Real", "Hard", "Physical"].includes(buttonType) && (
                    <Box>Button ID: {buttonId}</Box>
                  )}
                  <Box>Button Type: {convertDeviceType(buttonType)}</Box>
                </Stack>
                <PracticeSelector
                  setValue={setSelectedPractice}
                  value={currentPractice}
                />
                <Button
                  sx={{ padding: "10px 0" }}
                  onClick={() => setIsAddNewDialogOpen(true)}
                >
                  Create New Practice
                </Button>
                <AddNewPracticeDialog
                  open={isAddNewDialogOpen}
                  handleClose={() => setIsAddNewDialogOpen(false)}
                  refreshList={loadData}
                  regionsOptions={regionsOptions}
                  refreshRegionsOptions={refreshRegionsOptions}
                  setCreatedPractice={(value) => setSelectedPractice(value)}
                />
              </>
            )}
            {currentStep === steps.ASSIGN_PROVIDER && (
              <>
                <div
                  className="text-xs text-justify indent-4 mt-4"
                  style={{ maxWidth: "640px" }}
                >
                  {/* TODO - add provider description */}
                </div>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  gap={3}
                  sx={{ color: "gray" }}
                >
                  {["Real", "Hard", "Physical"].includes(buttonType) && (
                    <Box>Button ID: {buttonId}</Box>
                  )}
                  <Box>Button Type: {convertDeviceType(buttonType)}</Box>
                  <Box>Practice: {currentPractice?.name}</Box>
                </Stack>
                <ProviderSelector
                  providers={selectedProviders}
                  handleChangeProvider={handleChangeProvider}
                  isLoading={isProvidersLoading}
                  sx={{
                    maxHeight: "240px",
                    overflowY: "auto",
                  }}
                />
                <Button
                  sx={{ padding: "10px 0" }}
                  onClick={() => setIsAddNewProviderDialogOpen(true)}
                >
                  Create New Provider
                </Button>
                <AddNewProviderDialog
                  open={isAddNewProviderDialogOpen}
                  handleClose={() => setIsAddNewProviderDialogOpen(false)}
                  refreshList={loadData}
                  organizationId={practiceId}
                  setResultValue={(result) => {
                    setIsProvidersLoading(true);
                    setNewlyAddedProvider(result);
                  }}
                />
              </>
            )}
            {currentStep === steps.ASSIGN_SENDER && (
              <>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-start"}
                  gap={3}
                  sx={{ color: "gray" }}
                >
                  {["Real", "Hard", "Physical"].includes(buttonType) && (
                    <Box>Button ID: {buttonId}</Box>
                  )}
                  <Box>Button Type: {convertDeviceType(buttonType)}</Box>
                  <Box>Practice: {currentPractice?.name}</Box>
                </Stack>
                <SenderSelector
                  specialists={selectedSenders}
                  role={"Originator"}
                  handleChangeSpecialist={handleChangeSpecialist}
                  isLoading={isOriginatorsLoading}
                  onlyOne={["Real", "Hard", "Physical"].includes(deviceType)}
                  sx={{
                    maxHeight: "240px",
                    overflowY: "auto",
                  }}
                />
                <Button
                  sx={{ padding: "10px 0" }}
                  onClick={() => setIsAddNewDialogOpen(true)}
                >
                  Create New Sender
                </Button>
                <AddNewOriginatorDialog
                  open={isAddNewDialogOpen}
                  handleClose={() => setIsAddNewDialogOpen(false)}
                  refreshList={loadData}
                  organizationId={practiceId}
                  setResultValue={(result) => {
                    setIsOriginatorsLoading(true);
                    setNewlyAddedSender(result);
                  }}
                  role="Originator"
                />
              </>
            )}
            {currentStep === steps.ASSIGN_SPECIALISTS && (
              <Stack
                direction={isTablet ? "column" : "row"}
                gap={3}
                mt={3}
                minHeight={"150px"}
              >
                <SpecialistSelector
                  specialists={admins}
                  selectedSpecialistIds={selectedSpecialists}
                  role={"Admin"}
                  handleChangeSpecialist={handleChangeSpecialist}
                />
                <SpecialistSelector
                  specialists={marketers}
                  selectedSpecialistIds={selectedSpecialists}
                  role={"Marketer"}
                  handleChangeSpecialist={handleChangeSpecialist}
                />
                <SpecialistSelector
                  specialists={schedulers}
                  selectedSpecialistIds={selectedSpecialists}
                  role={"Scheduler"}
                  handleChangeSpecialist={handleChangeSpecialist}
                />
              </Stack>
            )}
            {currentStep === steps.ADD_BUTTON && (
              <Stack
                gap={1}
                direction={"row"}
                justifyContent={"center"}
                alignItems={"center"}
                mt={2}
                minHeight={"150px"}
              >
                <Slide4 />
                <Title sx={{ flex: "1 1 auto", fontSize: "1.4rem" }}>
                  Click the button below to Add a New{" "}
                  {["Virtual", "Soft", "Digital"].includes(
                    getValues("deviceType")
                  )
                    ? "Digital"
                    : "Physical"}{" "}
                  Button
                  {["Virtual", "Soft", "Digital"].includes(
                    getValues("deviceType")
                  )
                    ? " and notify Sender"
                    : ""}
                  .
                </Title>
              </Stack>
            )}
          </Stack>
        </form>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mb={2}
        >
          {/* <Box ml={2} sx={{ color: "gray" }}>
            <RequiredMark /> - These fields are required
          </Box> */}
          <Stack
            direction={"row"}
            flexWrap={"wrap"}
            justifyContent={"center"}
            gap={1}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={closeAndReset}
              sx={sx}
            >
              Cancel
            </Button>
            {selectedPractice && currentStep > 1 && (
              <Button
                variant="outlined"
                onClick={() => setCurrentStep(currentStep - 1)}
                sx={sx}
              >
                {"< "} Back
              </Button>
            )}
            {!selectedPractice && currentStep > steps.ASSIGN_PRACTICE && (
              <Button
                variant="outlined"
                onClick={() => setCurrentStep(currentStep - 1)}
                sx={sx}
              >
                {"< "} Back
              </Button>
            )}
            {FP.match(currentStep)
              .on(
                steps.ASSIGN_PRACTICE,
                <Button
                  variant="contained"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!practiceId || spinner}
                  sx={sx}
                >
                  Next
                </Button>
              )
              .on(
                steps.ASSIGN_PROVIDER,
                <Button
                  variant="contained"
                  onClick={handleCheckIfLocationExists}
                  disabled={spinner || !isProviderSelected}
                  sx={sx}
                >
                  Next
                </Button>
              )
              .on(
                steps.ASSIGN_SENDER,
                <Button
                  variant="contained"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={spinner || !isSenderSelected}
                  sx={sx}
                >
                  Next
                </Button>
              )
              .on(
                steps.ASSIGN_SPECIALISTS,
                ["Virtual", "Soft", "Digital"].includes(buttonType) ? (
                  <Button
                    variant="contained"
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={
                      !isSchedulerSelected &&
                      !isMarketerSelected &&
                      !isAdminSelected
                    }
                    sx={sx}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isUpdateButtonDisabled}
                    sx={sx}
                  >
                    Add New Button
                  </Button>
                )
              )
              .otherwise(
                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isUpdateButtonDisabled}
                  sx={sx}
                >
                  Add New Button
                </Button>
              )}
          </Stack>
        </Stack>
      }
    />
  );
};
